import React from 'react';
import { Link } from 'react-router-dom';
import { WEBSITE_PRINCIPLES, WEBSITE_TRUST_POINTS } from './websiteData';

const TIMELINE = [
  { year: '2023', title: 'The problem becomes personal', body: 'Our founder tried to adopt an open-source supply chain tool at a financial institution with 80,000+ repositories — three months to install, three hours per scan.' },
  { year: '2024', title: 'DepGuard ships', body: 'A single Go binary that parses SBOMs, scans for CVEs, checks licenses, and enforces policy in under 60 seconds.' },
  { year: '2025', title: 'Lexint is founded', body: 'PRISM added a Code Property Graph engine, Vigil brought taint analysis, and Lexint became the company behind all three products.' },
  { year: 'Now', title: 'Scaling for regulated enterprise', body: 'Shipping EU CRA compliance automation, AI-driven vulnerability triage, and cross-project impact analysis.' },
];

export default function WebsiteAboutPage() {
  return (
    <div className="dgw-page-wrap">

      {/* ── Hero ── */}
      <section className="dgw-hero" style={{textAlign:'center'}}>
        <div className="dgw-hero-inner" style={{maxWidth:860}}>
          <div className="dgw-badge">
            <span className="dgw-badge-dot" />
            About Lexint
          </div>
          <h1>We left enterprise tooling<br/>because it kept failing at enterprise scale.</h1>
          <p className="dgw-hero-lead" style={{maxWidth:640, margin:'0 auto 40px'}}>
            Lexint is built by engineers who ran security tooling at an 80,000-repo financial
            institution and found that every tool either crashed, took hours, or missed the point.
            The answer wasn't a better dashboard. It was a better engine.
          </p>
          <div className="dgw-hero-actions" style={{justifyContent:'center'}}>
            <Link to="/trial" className="dgw-btn dgw-btn-primary dgw-btn-lg">Start for free</Link>
            <Link to="/website/contact" className="dgw-btn dgw-btn-ghost dgw-btn-lg">Get in touch</Link>
          </div>
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <div className="dgw-2col" style={{gap:64}}>
            <div>
              <span className="dgw-section-tag">Our mission</span>
              <h2 style={{fontSize:'clamp(26px,3.5vw,40px)', fontWeight:800, letterSpacing:'-1px', color:'#0f172a', margin:'16px 0 20px'}}>
                Fragmented security tooling wastes engineering time.
              </h2>
              <p style={{fontSize:16, color:'#475569', lineHeight:1.8, marginBottom:20}}>
                We built Lexint because every security team we talked to was running three or four
                tools that did overlapping work. Different dashboards, different alert formats,
                different maintenance contracts. The CVEs that mattered got buried in noise.
              </p>
              <p style={{fontSize:16, color:'#475569', lineHeight:1.8}}>
                PRISM, Vigil, and DepGuard share one Code Property Graph. One parse step produces
                the data for vulnerability scanning, SBOM analysis, license compliance, and EU CRA
                audits. Fewer tools, less noise, faster shipping.
              </p>
            </div>
            <div style={{display:'flex', flexDirection:'column', gap:16}}>
              {[
                { n: '38ms',  label: 'CPG cold start', color: '#6366f1' },
                { n: '7',     label: 'Languages supported', color: '#8b5cf6' },
                { n: '140+',  label: 'Security rules', color: '#06b6d4' },
                { n: '0',     label: 'External services required', color: '#10b981' },
              ].map(s => (
                <div key={s.label} style={{
                  display:'flex', alignItems:'center', gap:20,
                  padding:'18px 24px', borderRadius:12,
                  background:'#f8fafc', border:'1px solid #e2e8f0',
                }}>
                  <span style={{fontSize:28, fontWeight:900, color:s.color, minWidth:60}}>{s.n}</span>
                  <span style={{fontSize:15, color:'#334155', fontWeight:600}}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Story timeline ── */}
      <section className="dgw-section" style={{background:'#f8fafc'}}>
        <div style={{maxWidth:860, margin:'0 auto'}}>
          <div className="dgw-section-header">
            <span className="dgw-section-tag">Our story</span>
            <h2>Built from real pain, not a whiteboard.</h2>
            <p className="dgw-section-lead">
              Lexint did not start with a venture pitch deck. It started with a team that 
              spent three months trying to install a competitor's product.
            </p>
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:0}}>
            {TIMELINE.map((t, i) => (
              <div key={t.year} style={{
                display:'grid', gridTemplateColumns:'80px 1fr',
                gap:28, paddingBottom: i < TIMELINE.length - 1 ? 40 : 0,
                position:'relative',
              }}>
                {/* Vertical line */}
                {i < TIMELINE.length - 1 && (
                  <div style={{
                    position:'absolute', left:39, top:36, bottom:0,
                    width:2, background:'#e2e8f0',
                  }} />
                )}
                {/* Year bubble */}
                <div style={{
                  width:40, height:40, borderRadius:'50%',
                  background:'#6366f1', display:'flex', alignItems:'center',
                  justifyContent:'center', flexShrink:0,
                  fontSize:11, fontWeight:800, color:'#fff',
                  letterSpacing:'-0.5px', zIndex:1,
                }}>{t.year}</div>
                <div style={{paddingTop:8}}>
                  <h3 style={{fontSize:17, fontWeight:700, color:'#0f172a', margin:'0 0 8px'}}>{t.title}</h3>
                  <p style={{fontSize:15, color:'#475569', lineHeight:1.7, margin:0}}>{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Principles ── */}
      <section className="dgw-section" style={{background:'#fff'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <div className="dgw-section-header">
            <span className="dgw-section-tag">Principles</span>
            <h2>What we believe.</h2>
            <p className="dgw-section-lead">These four principles drive every product decision we make.</p>
          </div>
          <div className="dgw-product-cards" style={{gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))'}}>
            {WEBSITE_PRINCIPLES.map((p, i) => {
              const colors = ['#6366f1','#8b5cf6','#06b6d4','#10b981'];
              const icons  = ['🔒','📐','🧑‍💻','📋'];
              return (
                <div key={p.title} style={{
                  padding:'28px 24px', borderRadius:16,
                  background:'#f8fafc', border:'1px solid #e2e8f0',
                  display:'flex', flexDirection:'column', gap:12,
                }}>
                  <div style={{fontSize:28}}>{icons[i]}</div>
                  <h3 style={{fontSize:16, fontWeight:700, color:'#0f172a', margin:0}}>{p.title}</h3>
                  <p style={{fontSize:14, color:'#475569', lineHeight:1.6, margin:0}}>{p.desc}</p>
                  <div style={{height:3, borderRadius:2, background:colors[i], width:40, marginTop:'auto'}} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Trust signals ── */}
      <section className="dgw-section" style={{background:'#0f172a'}}>
        <div style={{maxWidth:1100, margin:'0 auto'}}>
          <div className="dgw-section-header">
            <span className="dgw-section-tag" style={{background:'rgba(99,102,241,.15)', color:'#a5b4fc', borderColor:'rgba(99,102,241,.3)'}}>Why teams trust us</span>
            <h2 style={{color:'#f1f5f9'}}>Built for regulated environments.</h2>
            <p className="dgw-section-lead" style={{color:'#94a3b8'}}>
              DepGuard is designed for enterprise teams operating under EU CRA, NIST, 
              and ISO 27001 requirements.
            </p>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:20}}>
            {WEBSITE_TRUST_POINTS.map(tp => (
              <div key={tp.label} style={{
                padding:'24px', borderRadius:14,
                background:'rgba(255,255,255,0.04)',
                border:'1px solid rgba(255,255,255,0.08)',
              }}>
                <div style={{fontSize:13, fontWeight:700, color:'#6366f1', marginBottom:8, letterSpacing:'.5px', textTransform:'uppercase'}}>{tp.label}</div>
                <p style={{fontSize:14, color:'#94a3b8', lineHeight:1.6, margin:0}}>{tp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="dgw-cta-band">
        <div className="dgw-cta-band-inner">
          <h2>Try it with your own codebase.</h2>
          <p>Upload an SBOM or point PRISM at a repository. Results in under 60 seconds. No credit card required.</p>
          <div className="dgw-cta-band-actions">
            <Link to="/trial" className="dgw-btn-cta-white">Start free trial</Link>
            <Link to="/website/pricing" className="dgw-btn-cta-ghost">View pricing</Link>
            <Link to="/website/whitepaper" className="dgw-btn-cta-ghost">Read whitepaper</Link>
          </div>
        </div>
      </section>

    </div>
  );
}

